import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BriefcaseBusiness,
  ChevronDown,
  FileText,
  FolderUp,
  Github,
  Globe,
  Home,
  Info,
  LoaderCircle,
  LockKeyhole,
  Plus,
  Search,
  Settings,
  Share2,
  ShieldAlert,
  Smartphone,
  Sparkles,
  X,
} from 'lucide-react'
import { Link, NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { ProductBrand, ServiceFeatures } from '@aide/ui'
import { authBoundarySummary, type AuthSessionData } from '@aide/auth-client'
import { useAuthLinks, useAuthSession } from './hooks/useAuth'
import {
  createWorkspace,
  getIamStatus,
  getWorkspace,
  getWorkspaceEntitlements,
  listWorkspaces,
  promoteWorkspace,
  registerUser,
  runDeepAssessment,
  runQuickAssessment,
  sampleWorkspaceDetail,
  submitEvidencePack,
  updateWorkspaceSource,
  uploadWorkspaceAssets,
  type ManagedSourceSummary,
  type VhoopAnalysisReport,
  type VhoopEntitlementsResponse,
  type VhoopEvidenceCaptureSource,
  type VhoopExecutionProfileId,
  type VhoopFindingEvidence,
  type VhoopPromotionResponse,
  type VhoopSourceKind,
  type VhoopUpgradePath,
  type VhoopIAMStatus,
  type VhoopPlanId,
  type VhoopWorkspace,
  type VhoopWorkspaceDetail,
} from './services/api'

type WorkflowStepId = 'sources' | 'assessment' | 'report'
type StepStatus = 'pending' | 'ready' | 'active' | 'done' | 'error'
type OnboardingSourceKind = VhoopSourceKind

interface WorkspaceSourceCapture {
  source_kind: OnboardingSourceKind
  github_repo_url: string
  deployment_url: string
  notes: string
}

interface WorkspaceEvidencePackSummary {
  pack_id: string
  capture_source: string
  capture_label: string
  claim_count: number
  source_snapshot_id?: string
}

interface WorkspaceSourceSnapshotSummary {
  snapshot_id: string
  evidence_pack_id: string
  capture_source: string
  label: string
  storage_mode: string
}

interface WorkspaceOverview {
  workspace: VhoopWorkspace
  report: VhoopAnalysisReport | null
}

const ONBOARDING_STORAGE_KEY = 'vhoop-onboarding-draft'
const PLAN_RANK: Record<VhoopPlanId, number> = {
  free: 0,
  launch: 1,
  operate: 2,
  partner: 3,
}

function canUsePlan(accountPlan: VhoopPlanId | undefined, requested: VhoopPlanId) {
  return PLAN_RANK[accountPlan || 'free'] >= PLAN_RANK[requested]
}

function clearOnboardingDraft() {
  if (typeof window === 'undefined') {
    return
  }
  window.sessionStorage.removeItem(ONBOARDING_STORAGE_KEY)
}

function workspaceAssets(detail: VhoopWorkspaceDetail): Array<{ filename: string; size_bytes: number }> {
  const rawAssets = detail.workspace.intake_summary.assets
  if (!Array.isArray(rawAssets)) {
    return []
  }
  return rawAssets.filter((asset): asset is { filename: string; size_bytes: number } => (
    typeof asset === 'object'
    && asset !== null
    && typeof (asset as { filename?: unknown }).filename === 'string'
    && typeof (asset as { size_bytes?: unknown }).size_bytes === 'number'
  ))
}

function workspaceEvidencePacks(detail: VhoopWorkspaceDetail): WorkspaceEvidencePackSummary[] {
  const rawPacks = detail.workspace.intake_summary.evidence_packs
  if (!Array.isArray(rawPacks)) {
    return []
  }
  return rawPacks
    .filter((pack): pack is Record<string, unknown> => typeof pack === 'object' && pack !== null)
    .map((pack) => ({
      pack_id: typeof pack.pack_id === 'string' ? pack.pack_id : '',
      capture_source: typeof pack.capture_source === 'string' ? pack.capture_source : 'upload',
      capture_label: typeof pack.capture_label === 'string' ? pack.capture_label : 'Evidence Pack',
      claim_count: typeof pack.claim_count === 'number' ? pack.claim_count : 0,
      source_snapshot_id: typeof pack.source_snapshot_id === 'string' ? pack.source_snapshot_id : undefined,
    }))
    .filter((pack) => pack.pack_id)
}

function workspaceSourceSnapshots(detail: VhoopWorkspaceDetail): WorkspaceSourceSnapshotSummary[] {
  const rawSnapshots = detail.workspace.intake_summary.source_snapshots
  if (!Array.isArray(rawSnapshots)) {
    return []
  }
  return rawSnapshots
    .filter((snapshot): snapshot is Record<string, unknown> => typeof snapshot === 'object' && snapshot !== null)
    .map((snapshot) => ({
      snapshot_id: typeof snapshot.snapshot_id === 'string' ? snapshot.snapshot_id : '',
      evidence_pack_id: typeof snapshot.evidence_pack_id === 'string' ? snapshot.evidence_pack_id : '',
      capture_source: typeof snapshot.capture_source === 'string' ? snapshot.capture_source : 'upload',
      label: typeof snapshot.label === 'string' ? snapshot.label : 'Source snapshot',
      storage_mode: typeof snapshot.storage_mode === 'string' ? snapshot.storage_mode : 'reference_only',
    }))
    .filter((snapshot) => snapshot.snapshot_id)
}

function workspaceManagedSource(detail: VhoopWorkspaceDetail): ManagedSourceSummary | null {
  const raw = detail.workspace.intake_summary.forge_repo
  if (!raw || typeof raw !== 'object' || raw === null) {
    return null
  }
  const repo = raw as Partial<ManagedSourceSummary>
  if (typeof repo.repo_id !== 'string' || typeof repo.canonical_path !== 'string') {
    return null
  }
  return {
    repo_id: repo.repo_id,
    canonical_path: repo.canonical_path,
    display_name: typeof repo.display_name === 'string' ? repo.display_name : undefined,
    git_repo_name: typeof repo.git_repo_name === 'string' ? repo.git_repo_name : undefined,
    http_clone_url: typeof repo.http_clone_url === 'string' ? repo.http_clone_url : undefined,
    ssh_clone_url: typeof repo.ssh_clone_url === 'string' ? repo.ssh_clone_url : undefined,
    forge_workspace_url: typeof repo.forge_workspace_url === 'string' ? repo.forge_workspace_url : undefined,
  }
}

function workspaceSourceCapture(detail: VhoopWorkspaceDetail): WorkspaceSourceCapture {
  const raw = detail.workspace.intake_summary.source_capture
  if (!raw || typeof raw !== 'object' || raw === null) {
    return {
      source_kind: detail.workspace.source_kind,
      github_repo_url: '',
      deployment_url: '',
      notes: '',
    }
  }
  const source = raw as Record<string, unknown>
  return {
    source_kind: typeof source.source_kind === 'string'
      ? source.source_kind as OnboardingSourceKind
      : detail.workspace.source_kind,
    github_repo_url: typeof source.github_repo_url === 'string' ? source.github_repo_url : '',
    deployment_url: typeof source.deployment_url === 'string' ? source.deployment_url : '',
    notes: typeof source.notes === 'string' ? source.notes : '',
  }
}

function workspaceRepoPath(detail: VhoopWorkspaceDetail): string | null {
  const repo = detail.workspace.intake_summary.forge_repo
  if (!repo || typeof repo !== 'object' || repo === null) {
    return null
  }
  const canonicalPath = (repo as { canonical_path?: unknown }).canonical_path
  return typeof canonicalPath === 'string' ? canonicalPath : null
}

function workspaceHealthLabel(report: VhoopAnalysisReport | null): string {
  if (!report) {
    return 'No snapshot yet'
  }
  if (report.status !== 'completed') {
    return report.status
  }
  return report.readiness_label || 'Snapshot complete'
}

function workspaceRiskTone(report: VhoopAnalysisReport | null): 'low' | 'medium' | 'high' {
  if (!report || report.status !== 'completed' || typeof report.score !== 'number') {
    return 'medium'
  }
  if (report.score >= 70) {
    return 'low'
  }
  if (report.score >= 45) {
    return 'medium'
  }
  return 'high'
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)
}

function fallbackUpgradePaths(): VhoopUpgradePath[] {
  return sampleWorkspaceDetail.report?.upgrade_paths ?? []
}

function pillarStatusLabel(status: 'fragile' | 'emerging' | 'steady'): string {
  if (status === 'steady') {
    return 'Steady'
  }
  if (status === 'emerging') {
    return 'Emerging'
  }
  return 'Fragile'
}

function formatEvidenceLabel(evidence: VhoopFindingEvidence): string {
  if (evidence.kind === 'repo_file') {
    return `File: ${evidence.path || evidence.label}`
  }
  if (evidence.kind === 'source_snapshot') {
    return `Snapshot: ${evidence.label || evidence.source_snapshot_id || 'source snapshot'}`
  }
  if (evidence.kind === 'evidence_pack') {
    return `Evidence: ${evidence.path || evidence.label}`
  }
  if (evidence.kind === 'tool_output') {
    return `Proof: ${evidence.label}`
  }
  if (evidence.kind === 'missing_artifact') {
    return `Missing: ${evidence.label}`
  }
  return `Asset: ${evidence.label}`
}

function isActiveAnalysis(report: VhoopAnalysisReport | null | undefined): boolean {
  return report?.status === 'queued' || report?.status === 'running'
}

function hasSavedSource(detail: VhoopWorkspaceDetail | null): boolean {
  if (!detail) {
    return false
  }
  const source = workspaceSourceCapture(detail)
  return Boolean(
    workspaceAssets(detail).length
    || workspaceEvidencePacks(detail).length
    || source.github_repo_url
    || source.deployment_url
    || source.notes,
  )
}

function suggestOpenStep(detail: VhoopWorkspaceDetail | null): WorkflowStepId {
  if (!detail || !hasSavedSource(detail)) {
    return 'sources'
  }
  const report = detail.report
  if (report?.status === 'completed') {
    return 'report'
  }
  if (report?.status === 'queued' || report?.status === 'running' || report?.status === 'failed') {
    return 'assessment'
  }
  return 'assessment'
}

function stepTone(status: StepStatus): string {
  if (status === 'done') {
    return 'Done'
  }
  if (status === 'active') {
    return 'Live'
  }
  if (status === 'ready') {
    return 'Ready'
  }
  if (status === 'error') {
    return 'Issue'
  }
  return 'Pending'
}

const operatingModules = [
  { title: 'Application overview', eyebrow: 'The whole picture', copy: 'Sources, ownership, and business context form a single record for each application.', points: ['Source inventory', 'Application purpose', 'Ownership and responsibilities'] },
  { title: 'Readiness assessment', eyebrow: 'A clearer decision', copy: 'An evidence-based assessment identifies strengths, gaps, and the questions that still need an answer.', points: ['Readiness findings', 'Evidence coverage', 'Priority risks'] },
  { title: 'Verification records', eyebrow: 'Evidence behind the result', copy: 'Reports distinguish source analysis from execution results, so a documented claim is never mistaken for a completed check.', points: ['Source references', 'Check results', 'Unverified assumptions'] },
  { title: 'Release and recovery', eyebrow: 'Beyond the happy path', copy: 'Release, rollback, and recovery evidence contributes to a practical view of operational readiness.', points: ['Deployment evidence', 'Rollback preparation', 'Recovery documentation'] },
  { title: 'Review reports', eyebrow: 'A common understanding', copy: 'Findings and their supporting material give founders, engineers, and reviewers a shared basis for discussion.', points: ['Readiness summary', 'Supporting evidence', 'Open questions'] },
  { title: 'Next priorities', eyebrow: 'What matters next', copy: 'A focused list of gaps helps the team decide where further investigation or engineering work matters most.', points: ['Critical findings', 'Missing evidence', 'Recommended follow-up'] },
]

const useCases = [
  { title: 'A first product launch', subtitle: 'For founders', copy: 'An independent view of application evidence makes the remaining launch questions visible before the first customers depend on the product.', bullets: ['Readiness findings', 'Priority gaps', 'Questions for the engineering team'] },
  { title: 'A client handover', subtitle: 'For agencies', copy: 'A structured assessment gives the client a clearer picture of the application, its supporting material, and the work still outstanding.', bullets: ['Application overview', 'Delivery evidence', 'Outstanding risks'] },
  { title: 'A technical review', subtitle: 'For advisors and investors', copy: 'Source material and operational evidence support a review that goes beyond a product demonstration.', bullets: ['Evidence-backed findings', 'Explicit unknowns', 'Practical follow-up'] },
  { title: 'An inherited application', subtitle: 'For engineering teams', copy: 'A baseline assessment brings the knowns and unknowns together when a new team takes responsibility for existing software.', bullets: ['Source inventory', 'Ownership questions', 'Release and recovery gaps'] },
]

const planCards = [
  {
    tier: 'Free Snapshot',
    path: '/signup',
    summary: 'An initial assessment of available application evidence and its gaps.',
    bullets: ['Static evidence review', 'Evidence-based readiness summary', 'Open verification questions'],
    cta: 'Start free snapshot',
  },
  {
    tier: 'Launch Blueprint',
    path: '/upgrade/launch',
    summary: 'A deeper launch review, with verification options determined by workspace availability.',
    bullets: ['Build and manifest validation', 'Launch findings and open questions', 'Release and rollback checklist'],
    cta: 'Request launch blueprint',
  },
  {
    tier: 'Operate System',
    path: '/upgrade/operate',
    summary: 'An assessment path for teams reviewing an application beyond its first release.',
    bullets: ['Build, test, and health proof', 'Runbooks and ownership map', 'Follow-up assessment priorities'],
    cta: 'View operating review',
  },
  {
    tier: 'Partner Ops',
    path: '/upgrade/partner',
    summary: 'An extended review option. Scope and service availability require a separate agreement.',
    bullets: ['Remediation review', 'Operational reviews', 'Evidence and responsibility review'],
    cta: 'View partner option',
  },
]

const faqItems = [
  { question: 'What does VHoop assess?', answer: 'VHoop reviews application source material and supporting evidence for readiness gaps, including releases, access, data handling, and recovery. Findings identify both available evidence and unanswered questions.' },
  { question: 'Does a free snapshot run my code?', answer: 'No. The free snapshot analyzes supplied material without executing your application. Execution-based verification is separate and depends on the configured plan and runtime.' },
  { question: 'What does the score mean?', answer: 'The score summarizes the evidence and checks in that assessment. Missing evidence affects confidence. A score is not a security certification or a guarantee that an application will work in production.' },
  { question: 'Can I see a report before signing up?', answer: 'Yes. The sample assessment shows the report format, findings, and evidence. It uses demonstration data.' },
  { question: 'Does VHoop host or operate my application?', answer: 'The current workspace focuses on assessments and evidence. Managed hosting and ongoing operations require a separate service arrangement; they are not included automatically with an account.' },
]

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="page-shell product-site vhoop-site">
      <header className="product-container product-nav">
        <Link to="/" aria-label="VHoop home"><ProductBrand product="vhoop" /></Link>
        <nav aria-label="Main navigation"><Link className="product-secondary-nav" to="/features">The assessment</Link><Link className="product-secondary-nav" to="/use-cases">Who it’s for</Link><Link to="/workspace/sample">Sample report</Link><Link to="/login">Log in</Link><Link className="product-button" to="/signup">Get started</Link></nav>
      </header>
      {children}
      <footer className="product-container product-footer"><ProductBrand product="vhoop" /><span>Clarity before the next step.</span><div className="vhoop-footer-links"><Link to="/plans">Plans</Link><Link to="/security">Security</Link><Link to="/faq">FAQ</Link></div></footer>
    </div>
  )
}

function InternalAppLayout(
  { children, current = 'dashboard', userLabel, profile }:
  { children: ReactNode; current?: 'dashboard' | 'workspaces' | 'reports' | 'settings'; userLabel?: string | null; profile?: AuthSessionData | null },
) {
  const boundary = authBoundarySummary(profile || null)
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', to: '/dashboard' },
    { id: 'workspaces', label: 'Workspaces', to: '/workspaces' },
    { id: 'reports', label: 'Reports', to: '/reports' },
    { id: 'settings', label: 'Settings', to: '/settings' },
  ] as const

  return (
    <div className="internal-shell">
      <header className="internal-topbar">
        <div className="internal-topbar-inner">
          <div className="internal-topbar-left">
            <Link to="/" className="internal-brand" aria-label="Vhoop home">
              <ProductBrand product="vhoop" />
            </Link>
            <label className="internal-search">
              <Search className="icon-inline" />
              <input aria-label="Search workspaces" placeholder="Search workspaces" />
            </label>
            <nav className="internal-nav" aria-label="Dashboard navigation">
              {navItems.map((item) => (
                <NavLink
                  key={item.id}
                  className={({ isActive }) => `internal-nav-link${isActive || current === item.id ? ' internal-nav-link-active' : ''}`}
                  to={item.to}
                  end={item.id === 'dashboard'}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="internal-account">
            <div className="internal-boundary" title={`Vhoop boundary: ${boundary.tenantLabel}`}>
              <BadgeCheck className="icon-inline" />
              <div>
                <strong>{boundary.tenantLabel}</strong>
                <span>{boundary.organizationLabel || boundary.appTenantLabel || 'VHoop workspace'} · {boundary.adminScopeLabel}</span>
              </div>
            </div>
            <span className="internal-user-label">{userLabel || 'Vhoop user'}</span>
          </div>
        </div>
      </header>
      <div className="internal-main">
        {children}
      </div>
    </div>
  )
}

function Overlay(
  { title, eyebrow, open, onClose, children }:
  { title: string; eyebrow: string; open: boolean; onClose: () => void; children: ReactNode },
) {
  if (!open) {
    return null
  }

  return (
    <div className="overlay-backdrop" role="presentation" onClick={onClose}>
      <section className="overlay-panel" role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()}>
        <div className="overlay-head">
          <div>
            <span className="paper-kicker">{eyebrow}</span>
            <h2>{title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close overlay">
            <X className="icon-inline" />
          </button>
        </div>
        {children}
      </section>
    </div>
  )
}

function LandingView() {
  return (
    <Shell>
      <main className="product-container">
        <section className="product-hero">
          <div><span className="product-kicker">Application readiness, made clear</span><h1>A working demo.<br /><em>A ready business?</em></h1><p className="product-lead">VHoop turns application evidence into a clearer picture of readiness. What’s supported, what’s missing, and what deserves attention before customers depend on it.</p><div className="product-actions"><Link className="product-button" to="/signup">Start free snapshot <ArrowRight size={16} aria-hidden="true" /></Link><Link to="/workspace/sample">View a sample report <span aria-hidden="true">↗</span></Link></div><p className="product-subtle" style={{ marginTop: 18 }}>The first assessment reviews source material without running your code.</p></div>
          <div className="vhoop-assessment" aria-label="Illustrative readiness report">
            <div className="product-example-label"><span>Application readiness</span><span>Illustration</span></div>
            <h2>A clearer view<br />of what comes next.</h2>
            <div className="vhoop-assessment-row"><span>Source &amp; ownership</span><span className="vhoop-assessment-status">Evidence present</span></div>
            <div className="vhoop-assessment-row"><span>Release process</span><span className="vhoop-assessment-status pending">Needs review</span></div>
            <div className="vhoop-assessment-row"><span>Recovery plan</span><span className="vhoop-assessment-status missing">Evidence missing</span></div>
            <div className="vhoop-report-note"><span>THE NEXT QUESTION</span><p>What happens if the next release needs to be rolled back?</p></div>
            <Link to="/workspace/sample" className="vhoop-report-link">Inside a sample assessment <ArrowRight size={16} aria-hidden="true" /></Link>
          </div>
        </section>
        <section className="product-section"><span className="product-kicker">Confidence starts with evidence</span><h2>The questions around the code matter, too.</h2><ServiceFeatures>
          <article><FileText aria-hidden="true" /><h3>A view of the evidence</h3><p>Source files and supporting material give each assessment a concrete starting point.</p></article>
          <article><ShieldAlert aria-hidden="true" /><h3>Gaps in plain sight</h3><p>Readiness findings surface missing information and operational questions that a product demo can leave unanswered.</p></article>
          <article><BarChart3 aria-hidden="true" /><h3>A basis for the next decision</h3><p>A structured report helps founders, engineers, and advisors agree on the work that matters next.</p></article>
        </ServiceFeatures></section>
        <section className="product-section vhoop-closing"><div><span className="product-kicker">For the people responsible for the product</span><h2>From “it runs”<br />to “we understand it.”</h2><p className="product-lead">A first launch, a client handover, or an inherited application. VHoop makes the state of the evidence easier to discuss.</p></div><Link className="product-button" to="/use-cases">Who VHoop is for <ArrowRight size={16} aria-hidden="true" /></Link></section>
      </main>
    </Shell>
  )
}

function FeaturesView() {
  return (
    <Shell>
      <main className="container landing-page">
        <section className="workspace-hero">
          <div className="workspace-hero-main">
            <span className="eyebrow"><BadgeCheck className="icon-inline" /> Features</span>
            <h1>A closer look at application readiness.</h1>
            <p>
              VHoop brings application sources, readiness findings, and supporting evidence into a structured assessment. Each finding makes its basis—and its limits—visible.
            </p>
          </div>
          <div className="hero-score-card">
            <span className="panel-label">Assessment areas</span>
            <div className="score-number">6</div>
            <p>Six perspectives on the evidence behind an application.</p>
          </div>
        </section>

        <section className="landing-section">
          <div className="feature-list">
            {operatingModules.map((module) => (
              <article key={module.title} className="paper-section feature-detail-card">
                <span className="paper-kicker">{module.eyebrow}</span>
                <h2>{module.title}</h2>
                <p className="paper-copy">{module.copy}</p>
                <div className="paper-columns">
                  {module.points.map((point) => (
                    <div key={`${module.title}-${point}`} className="paper-box">
                      <h4>{point}</h4>
                      <p className="paper-copy">Part of the evidence reviewed in an application assessment.</p>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="cta-banner">
          <div>
            <span className="panel-label">Application operations</span>
            <h2>A clear starting point for the next decision.</h2>
          </div>
          <Link className="btn btn-primary" to="/signup">Start free snapshot <ArrowRight className="icon-inline" /></Link>
        </section>
      </main>
    </Shell>
  )
}

function UseCasesView() {
  return (
    <Shell>
      <main className="container landing-page">
        <section className="workspace-hero">
          <div className="workspace-hero-main">
            <span className="eyebrow"><BriefcaseBusiness className="icon-inline" /> Use cases</span>
            <h1>Useful wherever software has become a business responsibility.</h1>
            <p>
              Vhoop is especially valuable when a team needs to prove that an application can be
              launched, operated, recovered, and improved without depending on hope or one key person.
            </p>
          </div>
        </section>

        <section className="landing-section">
          <div className="feature-list">
            {useCases.map((useCase) => (
              <article key={useCase.title} className="paper-section feature-detail-card">
                <span className="paper-kicker">{useCase.subtitle}</span>
                <h2>{useCase.title}</h2>
                <p className="paper-copy">{useCase.copy}</p>
                <ul className="paper-list">
                  {useCase.bullets.map((bullet) => <li key={`${useCase.title}-${bullet}`}>{bullet}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </section>
      </main>
    </Shell>
  )
}

function PlansView() {
  return (
    <Shell>
      <main className="container landing-page">
        <section className="workspace-hero">
          <div className="workspace-hero-main">
            <span className="eyebrow"><BadgeCheck className="icon-inline" /> Plans</span>
            <h1>A starting point for every stage.</h1>
            <p>
              The free snapshot reviews application evidence without running code. Additional assessment options depend on the services and runtime available for your workspace.
            </p>
          </div>
        </section>
        <section className="paper-section">
          <div className="plan-grid">
            {planCards.map((plan) => (
              <article key={plan.tier} className="plan-card">
                <div className="plan-top">
                  <div>
                    <span className="plan-tier">{plan.tier}</span>
                    <h4>{plan.tier}</h4>
                  </div>
                </div>
                <p className="paper-copy">{plan.summary}</p>
                <ul className="paper-list">
                  {plan.bullets.map((bullet) => <li key={`${plan.tier}-${bullet}`}>{bullet}</li>)}
                </ul>
                <Link className="btn btn-primary" to={plan.path}>{plan.cta}</Link>
              </article>
            ))}
          </div>
        </section>
      </main>
    </Shell>
  )
}

function SecurityView() {
  return (
    <Shell>
      <main className="container landing-page">
        <section className="workspace-hero">
          <div className="workspace-hero-main">
            <span className="eyebrow"><ShieldAlert className="icon-inline" /> Trust and security</span>
            <h1>Operational evidence is sensitive. Vhoop treats it that way.</h1>
            <p>
              Vhoop is built for private application evidence: source context, operating gaps,
              proof runs, access notes, incidents, and customer trust material.
            </p>
          </div>
        </section>
        <section className="landing-section">
          <div className="landing-card-grid landing-card-grid-wide">
            <article className="glass-card marketing-card">
              <h3>No free-tier execution</h3>
              <p>Free snapshots never execute customer code. Static parsing and evidence review stay separate from paid runtime proof.</p>
            </article>
            <article className="glass-card marketing-card">
              <h3>Workspace access</h3>
              <p>Applications and reports belong to workspaces with account-based access.</p>
            </article>
            <article className="glass-card marketing-card">
              <h3>Controlled proof runs</h3>
              <p>Where enabled, execution checks use predefined profiles and limits. Reports identify which checks actually ran.</p>
            </article>
            <article className="glass-card marketing-card">
              <h3>Evidence transparency</h3>
              <p>Findings distinguish source evidence, missing material, and completed checks. The score follows a defined calculation.</p>
            </article>
            <article className="glass-card marketing-card">
              <h3>Supporting material</h3>
              <p>Reports bring findings and supporting evidence together for an informed review.</p>
            </article>
            <article className="glass-card marketing-card">
              <h3>Clear responsibilities</h3>
              <p>Application context records the people and responsibilities relevant to the assessment.</p>
            </article>
          </div>
        </section>
      </main>
    </Shell>
  )
}

function FaqView() {
  return (
    <Shell>
      <main className="container landing-page">
        <section className="workspace-hero">
          <div className="workspace-hero-main">
            <span className="eyebrow"><Sparkles className="icon-inline" /> FAQ</span>
            <h1>A few things worth knowing.</h1>
            <p>About the assessment, the evidence, and what a report can tell you.</p>
          </div>
        </section>
        <section className="paper-section">
          <div className="finding-list">
            {faqItems.map((item) => (
              <article key={item.question} className="finding-card">
                <strong>{item.question}</strong>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
    </Shell>
  )
}

function SignupView({ standalone = false }: { standalone?: boolean }) {
  const navigate = useNavigate()
  const { loginHref } = useAuthLinks('/dashboard')
  const { authenticated, loading, userLabel } = useAuthSession()
  const [fullName, setFullName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!loading && authenticated) {
      clearOnboardingDraft()
    }
  }, [authenticated, loading])

  async function handleSignup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await registerUser({
        app: 'vhoop',
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        company_name: companyName.trim() || 'Vhoop company',
      })
      clearOnboardingDraft()
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create account')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleCreateAsSignedInUser() {
    clearOnboardingDraft()
    navigate('/dashboard')
  }

  const stepTitle = standalone ? 'Create your Vhoop account' : 'Create your Vhoop account'
  const leadCopy = standalone
    ? 'A private workspace for your applications, assessments, and the evidence behind them.'
    : 'A private workspace for your applications, assessments, and the evidence behind them.'

  return (
    <Shell>
      <main className="container auth-shell">
        <div className="auth-split">
        <section className="auth-hero">
          <span className="eyebrow"><LockKeyhole className="icon-inline" /> Private dashboard</span>
          <h1>{stepTitle}</h1>
          <p className="hero-lead">{leadCopy}</p>
          <div className="bullet-grid">
            <div className="bullet-row">
              <span className="bullet-dot">01</span>
              <div>
                <strong>Your applications</strong>
                <p>A dedicated home for your company’s application assessments.</p>
              </div>
            </div>
            <div className="bullet-row">
              <span className="bullet-dot">02</span>
              <div>
                <strong>The supporting evidence</strong>
                <p>Sources and business context stay alongside the application they describe.</p>
              </div>
            </div>
            <div className="bullet-row">
              <span className="bullet-dot">03</span>
              <div>
                <strong>A clearer picture</strong>
                <p>Readiness reports bring findings, gaps, and next priorities together.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="auth-panel">
          {error ? <div className="error-banner">{error}</div> : null}

          {!loading && authenticated ? (
            <div className="mini-surface">
              <h3>Signed in{userLabel ? ` as ${userLabel}` : ''}</h3>
              <p className="surface-copy">Continue to your Vhoop dashboard to create or open an application hub.</p>
              <div className="hero-actions">
                <button className="btn btn-primary" type="button" onClick={handleCreateAsSignedInUser} disabled={submitting}>
                  Go to dashboard <ArrowRight className="icon-inline" />
                </button>
              </div>
            </div>
          ) : (
            <form className="workflow-form" onSubmit={handleSignup}>
              <h2>Sign up</h2>
              <label className="field-label" htmlFor="full-name">
                Full name
              </label>
              <input
                id="full-name"
                className="text-input"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                placeholder="Marcel Gocke"
                required
              />

              <label className="field-label" htmlFor="signup-company-name">
                Company name
              </label>
              <input
                id="signup-company-name"
                className="text-input"
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                placeholder="Northwind Commerce"
                required
              />

              <label className="field-label" htmlFor="signup-email">
                Work email
              </label>
              <input
                id="signup-email"
                className="text-input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="founder@example.com"
                required
              />

              <label className="field-label" htmlFor="signup-password">
                Password
              </label>
              <input
                id="signup-password"
                className="text-input"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="At least 8 characters"
                required
              />

              <label className="field-label" htmlFor="confirm-password">
                Confirm password
              </label>
              <input
                id="confirm-password"
                className="text-input"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Repeat password"
                required
              />

              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? <><LoaderCircle className="icon-inline spinning" /> Creating account</> : <>Create account <ArrowRight className="icon-inline" /></>}
              </button>

              <p className="surface-copy">
                Already have an account? <a className="inline-link" href={loginHref}>Log in</a>
              </p>
            </form>
          )}
        </section>
        </div>
      </main>
    </Shell>
  )
}

function WorkflowRail(
  { currentStep, sourceStatus, assessmentStatus, reportStatus }:
  {
    currentStep: WorkflowStepId
    sourceStatus: StepStatus
    assessmentStatus: StepStatus
    reportStatus: StepStatus
  },
) {
  const steps: Array<{ id: WorkflowStepId; title: string; status: StepStatus }> = [
    { id: 'sources', title: 'Sources', status: sourceStatus },
    { id: 'assessment', title: 'Snapshot', status: assessmentStatus },
    { id: 'report', title: 'Readout', status: reportStatus },
  ]

  return (
    <aside className="workflow-rail">
      <span className="panel-label">Workflow</span>
      <div className="rail-list">
        {steps.map((step, index) => (
          <div key={step.id} className={`rail-item${currentStep === step.id ? ' rail-item-active' : ''}`}>
            <span className={`rail-index rail-index-${step.status}`}>{index + 1}</span>
            <div>
              <strong>{step.title}</strong>
              <small>{stepTone(step.status)}</small>
            </div>
          </div>
        ))}
      </div>
    </aside>
  )
}

function AccordionStep(
  {
    step,
    title,
    subtitle,
    status,
    open,
    onOpen,
    children,
  }:
  {
    step: string
    title: string
    subtitle: string
    status: StepStatus
    open: boolean
    onOpen: () => void
    children: ReactNode
  },
) {
  return (
    <section className={`accordion-card${open ? ' accordion-card-open' : ''}`}>
      <button className="accordion-head" type="button" onClick={onOpen}>
        <div className="accordion-meta">
          <span className={`status-chip status-chip-${status}`}>{step}</span>
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
        </div>
        <ChevronDown className={`accordion-icon${open ? ' accordion-icon-open' : ''}`} />
      </button>
      {open ? <div className="accordion-body">{children}</div> : null}
    </section>
  )
}

function UpgradePlansSection({ report }: { report: VhoopAnalysisReport | null }) {
  const upgradePaths = report?.upgrade_paths.length ? report.upgrade_paths : fallbackUpgradePaths()

  return (
    <section className="paper-section">
      <div className="paper-section-head">
        <div>
          <span className="paper-kicker">Operating plans</span>
          <h3>Ways to continue</h3>
        </div>
      </div>
      <div className="plan-grid">
        {upgradePaths.map((path) => (
          <article key={path.tier_id} className={`plan-card${path.recommended ? ' plan-card-featured' : ''}`}>
            <div className="plan-top">
              <div>
                <span className="plan-tier">{path.tier_id}</span>
                <h4>{path.name}</h4>
              </div>
              {path.recommended ? <span className="paper-badge">Recommended</span> : null}
            </div>
            <p className="plan-tagline">{path.tagline}</p>
            <p className="paper-copy">{path.outcome_summary}</p>
            <ul className="paper-list">
              {path.deliverables.map((deliverable) => <li key={`${path.tier_id}-${deliverable}`}>{deliverable}</li>)}
            </ul>
            <Link className="btn btn-primary" to={`/upgrade/${path.tier_id}`}>{path.cta_label}</Link>
          </article>
        ))}
      </div>
    </section>
  )
}

function ReportSnapshot({ report }: { report: VhoopAnalysisReport }) {
  const [methodOpen, setMethodOpen] = useState(false)
  const implementationQuality = report.implementation_quality
  const scanResults = report.scan_results ?? []
  const executionResults = report.execution_results ?? []
  const scoreBreakdown = report.score_breakdown ?? []
  const vmUsage = report.vm_usage ?? {}
  const llmUsage = report.llm_usage ?? {}
  const entitlement = report.entitlement_snapshot ?? {}
  const rawScore = typeof report.raw_score === 'number' ? report.raw_score : report.score ?? 0
  const confidenceCap = typeof report.confidence_cap === 'number' ? report.confidence_cap : 55
  const evidenceConfidenceCap = typeof report.evidence_confidence_cap === 'number'
    ? report.evidence_confidence_cap
    : (report.trace && typeof report.trace.evidence_confidence_cap === 'number' ? report.trace.evidence_confidence_cap : confidenceCap)
  const planCap = typeof report.plan_cap === 'number'
    ? report.plan_cap
    : (report.trace && typeof report.trace.plan_cap === 'number' ? report.trace.plan_cap : confidenceCap)
  const criticalPenalties = report.critical_penalties ?? []
  const monthlyVmRuns = Number(vmUsage.monthly_vm_runs ?? entitlement.monthly_vm_runs ?? 0)
  const monthlyLlmCalls = Number(llmUsage.monthly_llm_calls ?? entitlement.monthly_llm_calls ?? 0)
  const usedFallback = report.trace?.mode === 'heuristic_fallback' || report.trace?.mode === 'static_only'
  const fallbackReason = typeof report.trace?.agent_error === 'string' && report.trace.agent_error.includes('budget exhausted')
    ? 'Narrative synthesis was unavailable because the current request budget is exhausted. Vhoop completed a deterministic static snapshot instead.'
    : 'Vhoop completed this snapshot with deterministic static analysis. Add sources or rerun when narrative synthesis is available for a richer readout.'
  const freeExecutionLocked = report.plan_id === 'free' && monthlyVmRuns === 0
  const llmBudgetExhausted = typeof report.trace?.agent_error === 'string' && report.trace.agent_error.includes('budget exhausted')

  return (
    <>
    <div className="report-sheet">
      <div className="report-sheet-sidebar">
        <div className="report-score">
          <span className="paper-kicker">Operating snapshot</span>
          <div className="report-score-number">{report.score ?? 0}</div>
          <p>{report.readiness_label}</p>
        </div>
        <div className="paper-box">
          <span className="paper-kicker">Plan usage</span>
          <ul className="paper-list">
            <li>Runtime proof runs: {String(vmUsage.vm_runs_used_this_month ?? 0)} / {String(monthlyVmRuns)}</li>
            <li>Narrative analysis calls: {String(llmUsage.llm_calls_used_this_month ?? 0)} / {String(monthlyLlmCalls)}</li>
            <li>Recurring checks: {vmUsage.recurring_checks_enabled ? 'enabled' : 'locked'}</li>
          </ul>
          <p className="usage-note">
            {freeExecutionLocked ? 'Free snapshots never execute customer code. Runtime proof starts in paid plans.' : 'Runtime proof usage is counted when an execution profile is attempted.'}
            {' '}
            {llmBudgetExhausted ? 'Narrative synthesis was attempted, but the current analysis budget is exhausted.' : 'The score itself is deterministic; narrative analysis only improves the explanation.'}
          </p>
        </div>
        <div className="paper-box">
          <span className="paper-kicker">Top operating risks</span>
          <ul className="paper-list">
            {report.top_risks.map((risk) => <li key={risk}>{risk}</li>)}
          </ul>
        </div>
      </div>

      <div className="report-sheet-main">
        <section className="paper-section">
          <div className="paper-section-head">
            <div>
              <span className="paper-kicker">Summary</span>
              <h3>Operating summary</h3>
            </div>
          </div>
          <p className="paper-copy">{report.executive_summary}</p>
          {usedFallback ? (
            <div className="analysis-notice">
              <strong>Static fallback snapshot</strong>
              <p>{fallbackReason}</p>
            </div>
          ) : null}
          {scoreBreakdown.length ? (
            <div className="score-method-card">
              <div>
                <span className="paper-kicker">Score method</span>
                <h4>Trust-adjusted evidence score</h4>
                <p>
                  Raw readiness is {rawScore}. Evidence cap is {evidenceConfidenceCap}. Plan cap is {planCap}. Effective cap is {confidenceCap}.
                  Critical penalties subtract {criticalPenalties.reduce((total, penalty) => total + Number(penalty.points || 0), 0)}. Narrative synthesis does not set the number.
                </p>
              </div>
              <div className="score-method-side">
                <div className="score-method-total">{report.score ?? 0}/100</div>
                <button className="btn btn-secondary btn-compact" type="button" onClick={() => setMethodOpen(true)}>
                  <Info className="icon-inline" /> View checks
                </button>
              </div>
            </div>
          ) : null}
          {criticalPenalties.length ? (
            <div className="analysis-notice analysis-notice-risk">
              <strong>Critical penalties applied</strong>
              <ul className="paper-list">
                {criticalPenalties.map((penalty) => (
                  <li key={`${penalty.id || penalty.title}-${penalty.points}`}>
                    -{penalty.points}: {penalty.title}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="paper-columns">
            <div className="paper-box">
              <h4>What looks good</h4>
              <ul className="paper-list">
                {report.strengths.map((strength) => <li key={strength}>{strength}</li>)}
              </ul>
            </div>
            <div className="paper-box">
              <h4>What to do next</h4>
              <ul className="paper-list">
                {report.next_steps.map((step) => <li key={step}>{step}</li>)}
              </ul>
            </div>
          </div>
        </section>

        {implementationQuality ? (
          <section className="paper-section">
            <div className="paper-section-head">
              <div>
              <span className="paper-kicker">Change safety</span>
                <h3>Implementation quality</h3>
              </div>
              <span className="paper-badge">{implementationQuality.evidence_status}</span>
            </div>
            <div className="paper-columns">
              <div className="paper-box">
                <h4>{implementationQuality.score} · {implementationQuality.label}</h4>
                <p className="paper-copy">{implementationQuality.summary}</p>
              </div>
              <div className="paper-box">
                <h4>Signals</h4>
                <ul className="paper-list">
                  {implementationQuality.signals.map((signal) => (
                    <li key={`${signal.label}-${signal.status}`}>{signal.label}: {signal.status}</li>
                  ))}
                </ul>
              </div>
            </div>
            {implementationQuality.locked_checks.length ? (
              <div className="trace-summary">
                {implementationQuality.locked_checks.slice(0, 4).map((check) => (
                  <span key={`${check.profile_id}-${check.plan_required}`}>{check.title} requires {check.plan_required}</span>
                ))}
              </div>
            ) : null}
            {implementationQuality.ai_development_signals.length ? (
              <div className="finding-list">
                {implementationQuality.ai_development_signals.map((signal) => (
                  <article key={`${signal.label}-${signal.status}`} className="finding-card">
                    <span className="severity severity-low">{signal.status}</span>
                    <strong>{signal.label}</strong>
                    <p>{signal.detail}</p>
                  </article>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        {executionResults.length ? (
          <section className="paper-section">
            <div className="paper-section-head">
              <div>
                <span className="paper-kicker">Proof</span>
                <h3>Runtime proof</h3>
              </div>
              <span className="paper-badge">{report.execution_provider || 'none'}</span>
            </div>
            <div className="finding-list">
              {executionResults.map((result) => (
                <article key={`${result.profile_id}-${result.status}`} className="finding-card">
                  <span className={`severity severity-${result.status === 'succeeded' ? 'low' : result.status === 'failed' ? 'high' : 'medium'}`}>{result.status}</span>
                  <strong>{result.profile_name}</strong>
                  <p>{result.summary || 'No execution output was captured for this profile.'}</p>
                  <div className="trace-summary">
                    {result.command.length ? <span>{result.command.join(' ')}</span> : <span>Command locked or not run</span>}
                    {typeof result.exit_code === 'number' ? <span>Exit {result.exit_code}</span> : null}
                    {typeof result.duration_ms === 'number' ? <span>{result.duration_ms} ms</span> : null}
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section className="paper-section">
          <div className="paper-section-head">
            <div>
              <span className="paper-kicker">Pillars</span>
              <h3>Operating pillars</h3>
            </div>
          </div>
          <div className="pillar-grid">
            {report.pillars.map((pillar) => (
              <article key={pillar.slug} className="pillar-card">
                <div className="pillar-head">
                  <strong>{pillar.title}</strong>
                  <span className={`pillar-status pillar-status-${pillar.status}`}>{pillarStatusLabel(pillar.status)}</span>
                </div>
                <div className="pillar-score">{pillar.score}</div>
                <p>{pillar.summary}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="paper-section">
          <div className="paper-section-head">
            <div>
              <span className="paper-kicker">Findings</span>
              <h3>Operating findings</h3>
            </div>
          </div>
          <div className="finding-list">
            {report.findings.map((finding) => (
              <article key={finding.title} className="finding-card">
                <span className={`severity severity-${finding.severity}`}>{finding.severity}</span>
                <strong>{finding.title}</strong>
                <p>{finding.summary}</p>
                {finding.evidence.length ? (
                  <div className="evidence-list">
                    {finding.evidence.map((evidence) => (
                      evidence.url ? (
                        <a
                          key={`${finding.title}-${evidence.kind}-${evidence.label}`}
                          className="evidence-pill evidence-link"
                          href={evidence.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {formatEvidenceLabel(evidence)}
                        </a>
                      ) : (
                        <span key={`${finding.title}-${evidence.kind}-${evidence.label}`} className="evidence-pill">
                          {formatEvidenceLabel(evidence)}
                        </span>
                      )
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <UpgradePlansSection report={report} />
      </div>
    </div>
    <Overlay
      open={methodOpen}
      onClose={() => setMethodOpen(false)}
      eyebrow="Evidence model"
      title="What Vhoop checked"
    >
      <div className="overlay-content">
        <p className="paper-copy">
          The score is calculated from deterministic checks. Narrative analysis can explain the result,
          but it does not choose the number.
        </p>
        {scoreBreakdown.length ? (
          <div className="score-breakdown-list">
            {scoreBreakdown.map((item) => (
              <article key={item.category} className="score-breakdown-row">
                <div className="score-breakdown-top">
                  <strong>{item.category}</strong>
                  <span>{item.earned} / {item.max_score}</span>
                </div>
                <div className="score-breakdown-track" aria-hidden="true">
                  <span style={{ width: `${Math.max(0, Math.min(100, (item.earned / Math.max(1, item.max_score)) * 100))}%` }} />
                </div>
                <p>{item.summary}</p>
                {item.check_contributions?.length ? (
                  <div className="trace-summary score-formula">
                    {item.check_contributions.slice(0, 4).map((check) => (
                      <span key={`${item.category}-${check.check_id}`}>
                        {check.check_id}: {check.earned}/{check.max_score} via {check.source}
                      </span>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        ) : null}
        {scanResults.length ? (
          <div className="finding-list">
            {scanResults.map((check) => (
              <article key={check.check_id} className="finding-card">
                <span className={`severity severity-${check.severity === 'info' ? 'low' : check.severity}`}>{check.status}</span>
                <strong>{check.title}</strong>
                <p>{check.summary}</p>
                <div className="evidence-list">
                  <span className="evidence-pill">Confidence {Math.round(Number(check.confidence || 0) * 100)}%</span>
                  {check.evidence_refs.slice(0, 3).map((evidence) => (
                    <span key={`${check.check_id}-${evidence.kind}-${evidence.label}`} className="evidence-pill">
                      {formatEvidenceLabel(evidence)}
                    </span>
                  ))}
                </div>
                {check.plan_required ? <span className="evidence-pill">Unlocks with {check.plan_required}</span> : null}
              </article>
            ))}
          </div>
        ) : null}
      </div>
    </Overlay>
    </>
  )
}

function DashboardView({ view = 'dashboard' }: { view?: 'dashboard' | 'workspaces' | 'reports' | 'settings' }) {
  const navigate = useNavigate()
  const { loginHref } = useAuthLinks('/dashboard')
  const { authenticated, loading, userLabel, profile } = useAuthSession()
  const [overview, setOverview] = useState<WorkspaceOverview[]>([])
  const [iamStatus, setIamStatus] = useState<VhoopIAMStatus | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [creating, setCreating] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [checksOpen, setChecksOpen] = useState(false)
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (loading || !authenticated) {
      setLoadingWorkspaces(false)
      return
    }
    let active = true
    setLoadingWorkspaces(true)
    setError(null)
    void Promise.allSettled([listWorkspaces(), getIamStatus()])
      .then(async ([workspaceResult, iamResult]) => {
        if (!active) {
          return
        }
        if (iamResult.status === 'fulfilled') {
          setIamStatus(iamResult.value)
        }
        if (workspaceResult.status !== 'fulfilled') {
          setError(workspaceResult.reason instanceof Error ? workspaceResult.reason.message : 'Could not load workspaces')
          setOverview([])
          return
        }
        const details = await Promise.allSettled(workspaceResult.value.map((workspace) => getWorkspace(workspace.workspace_id)))
        if (!active) {
          return
        }
        setOverview(workspaceResult.value.map((workspace, index) => {
          const result = details[index]
          if (result?.status === 'fulfilled') {
            return { workspace: result.value.workspace, report: result.value.report }
          }
          return { workspace, report: null }
        }))
      })
      .catch((err) => {
        if (active) {
          setError(err instanceof Error ? err.message : 'Could not load dashboard')
        }
      })
      .finally(() => {
        if (active) {
          setLoadingWorkspaces(false)
        }
      })
    return () => {
      active = false
    }
  }, [authenticated, loading])

  async function handleCreateWorkspace(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setCreating(true)
    setError(null)
    try {
      const workspace = await createWorkspace({
        display_name: displayName.trim(),
        description: description.trim(),
        source_kind: 'upload',
        provision_repo: true,
        intake_summary: {
          dashboard_created: true,
        },
      })
      setCreateOpen(false)
      navigate(`/workspace/${workspace.workspace_id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create workspace')
    } finally {
      setCreating(false)
    }
  }

  if (!loading && !authenticated) {
    return (
      <Shell>
        <main className="container auth-shell">
          <section className="center-card center-card-wide">
            <span className="eyebrow"><LockKeyhole className="icon-inline" /> Sign in required</span>
            <h1>Create an account to open your dashboard.</h1>
            <p className="hero-lead">Your dashboard is where workspaces, sources, operating snapshots, proof runs, and upgrades live.</p>
            <div className="hero-actions">
              <Link className="btn btn-primary" to="/signup">Create account</Link>
              <a className="btn btn-secondary" href={loginHref}>Log in</a>
            </div>
          </section>
        </main>
      </Shell>
    )
  }

  const workspaces = overview.map((item) => item.workspace)
  const reports = overview.map((item) => item.report).filter((report): report is VhoopAnalysisReport => Boolean(report))
  const completedReports = reports.filter((report) => report.status === 'completed')
  const assessedCount = completedReports.length
  const averageScore = completedReports.length
    ? Math.round(completedReports.reduce((total, report) => total + Number(report.score ?? 0), 0) / completedReports.length)
    : 0
  const highRiskCount = completedReports.filter((report) => Number(report.score ?? 0) < 45).length
  const sourceCount = workspaces.filter((workspace) => workspace.source_kind !== 'unknown' || Boolean(workspace.linked_repo_id)).length
  const newestWorkspace = [...workspaces].sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))[0]

  const headerCopy = {
    dashboard: {
      eyebrow: 'Dashboard',
      title: 'All workspaces',
      copy: 'Track every product you want to host, operate, and improve. One workspace represents one project with one repository or code base.',
    },
    workspaces: {
      eyebrow: 'Workspaces',
      title: 'Workspace registry',
      copy: 'Create and manage one operating workspace per product, repository, or code base.',
    },
    reports: {
      eyebrow: 'Reports',
      title: 'Operating readouts',
      copy: 'Review the latest Vhoop snapshots across all workspaces and open the underlying project.',
    },
    settings: {
      eyebrow: 'Settings',
      title: 'Workspace settings',
      copy: 'Review the tenant boundary and operating controls used by this Vhoop account.',
    },
  }[view]

  return (
    <InternalAppLayout current={view} userLabel={userLabel} profile={profile}>
      <main className="dashboard-page">
        <header className="dashboard-header">
          <div>
            <span className="eyebrow"><Home className="icon-inline" /> {headerCopy.eyebrow}</span>
            <h1>{headerCopy.title}</h1>
            <p>{headerCopy.copy}</p>
          </div>
          <div className="dashboard-actions">
            <button className="btn btn-secondary" type="button" onClick={() => setChecksOpen(true)}>
              <Info className="icon-inline" /> What Vhoop checks
            </button>
            <button className="btn btn-primary" type="button" onClick={() => setCreateOpen(true)}>
              <Plus className="icon-inline" /> New workspace
            </button>
          </div>
        </header>

        {error ? <div className="error-banner workspace-error">{error}</div> : null}

        {view === 'dashboard' ? <section className="dashboard-stat-grid" aria-label="Workspace overview">
          <article className="dashboard-stat-card">
            <span className="panel-label">Workspaces</span>
            <strong>{workspaces.length}</strong>
            <p>{newestWorkspace ? `Latest update: ${newestWorkspace.display_name} on ${formatDate(newestWorkspace.updated_at)}` : 'Create the first workspace to start.'}</p>
          </article>
          <article className="dashboard-stat-card">
            <span className="panel-label">Assessed</span>
            <strong>{assessedCount}</strong>
            <p>{assessedCount ? 'Workspace snapshots completed.' : 'No operating snapshots completed yet.'}</p>
          </article>
          <article className="dashboard-stat-card">
            <span className="panel-label">Average score</span>
            <strong>{assessedCount ? averageScore : '--'}</strong>
            <p>{assessedCount ? 'Comparable across workspaces using the same evidence model.' : 'Run a snapshot to establish the baseline.'}</p>
          </article>
          <article className="dashboard-stat-card">
            <span className="panel-label">Attention</span>
            <strong>{highRiskCount}</strong>
            <p>{highRiskCount ? 'Workspace snapshots need immediate operating follow-up.' : 'No high-risk completed snapshots.'}</p>
          </article>
        </section> : null}

        {view === 'dashboard' || view === 'workspaces' ? <section id="workspaces" className="dashboard-section">
          <div className="dashboard-section-head">
            <div>
              <span className="paper-kicker">Projects</span>
              <h2>Workspace overview</h2>
            </div>
            <span className="dashboard-muted">{sourceCount} with source or managed repo attached</span>
          </div>

          {loadingWorkspaces ? (
            <div className="dashboard-empty">
              <LoaderCircle className="icon-inline spinning" /> Loading workspaces
            </div>
          ) : overview.length ? (
            <div className="workspace-card-grid">
              {overview.map(({ workspace, report }) => {
                const tone = workspaceRiskTone(report)
                const score = report?.status === 'completed' ? report.score ?? 0 : null
                const strengths = report?.strengths?.slice(0, 2) ?? []
                const risks = report?.top_risks?.slice(0, 2) ?? []
                return (
                  <article key={workspace.workspace_id} className="workspace-overview-card">
                    <div className="workspace-card-top">
                      <div>
                        <span className={`severity severity-${tone}`}>{workspace.status}</span>
                        <h3>{workspace.display_name}</h3>
                      </div>
                      <div className="workspace-score-badge">
                        <strong>{score ?? '--'}</strong>
                        <span>{score === null ? 'pending' : 'score'}</span>
                      </div>
                    </div>
                    <p className="workspace-description">{workspace.description || 'No project description yet.'}</p>
                    <p className="workspace-health">{workspaceHealthLabel(report)}</p>
                    <div className="workspace-signal-grid">
                      <div>
                        <span className="paper-kicker">Good</span>
                        {strengths.length ? (
                          <ul className="compact-list">
                            {strengths.map((strength) => <li key={strength}>{strength}</li>)}
                          </ul>
                        ) : <p>No positive evidence yet.</p>}
                      </div>
                      <div>
                        <span className="paper-kicker">Needs attention</span>
                        {risks.length ? (
                          <ul className="compact-list">
                            {risks.map((risk) => <li key={risk}>{risk}</li>)}
                          </ul>
                        ) : <p>Run a snapshot to surface risks.</p>}
                      </div>
                    </div>
                    <div className="workspace-card-footer">
                      <span>Updated {formatDate(workspace.updated_at)}</span>
                      <Link className="btn btn-secondary btn-compact" to={`/workspace/${workspace.workspace_id}`}>Open</Link>
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="dashboard-empty">
              <BriefcaseBusiness className="empty-icon" />
              <h3>No workspaces yet</h3>
              <p>Create a workspace for each app or code base you want Vhoop to operate around.</p>
              <button className="btn btn-primary" type="button" onClick={() => setCreateOpen(true)}>
                <Plus className="icon-inline" /> Create workspace
              </button>
            </div>
          )}
        </section> : null}

        {view === 'dashboard' || view === 'reports' || view === 'settings' ? <section id="reports" className={`dashboard-section ${view === 'dashboard' ? 'dashboard-two-column' : ''}`}>
          {view !== 'settings' ? (
          <article className="dashboard-panel">
            <span className="paper-kicker">Latest reports</span>
            <h2>Recent operating readouts</h2>
            {completedReports.length ? (
              <div className="dashboard-report-list">
                {overview.filter((item) => item.report?.status === 'completed').slice(0, 5).map(({ workspace, report }) => (
                  <Link key={workspace.workspace_id} className="dashboard-report-row" to={`/workspace/${workspace.workspace_id}`}>
                    <span>{workspace.display_name}</span>
                    <strong>{report?.score ?? '--'}</strong>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="paper-copy">No completed reports yet. Open a workspace, add sources, and run the first snapshot.</p>
            )}
          </article>
          ) : null}
          {view !== 'reports' ? (
          <article id="settings" className="dashboard-panel">
            <span className="paper-kicker">Tenant boundary</span>
            <h2>{iamStatus?.legal_entity_name || 'Vhoop AI'} identity</h2>
            <p className="paper-copy">{iamStatus?.isolation_model || 'Vhoop keeps workspaces, reports, sources, and usage scoped to the customer tenant.'}</p>
            <div className="trace-summary">
              <span>{iamStatus?.realm_mode === 'shared_realm' ? 'Shared realm mode' : 'Separate realm default'}</span>
              <span>{iamStatus?.provisioning_status || 'loading'}</span>
            </div>
          </article>
          ) : null}
        </section> : null}
      </main>

      <Overlay
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        eyebrow="New workspace"
        title="Create workspace"
      >
        <form className="workflow-form dashboard-create-form" onSubmit={handleCreateWorkspace}>
          <p className="paper-copy">Create one workspace per product, repository, or code base. You can attach GitHub, managed source, a live URL, or uploads after creation.</p>
          <label className="field-label" htmlFor="dashboard-display-name">
            Workspace name
          </label>
          <input
            id="dashboard-display-name"
            className="text-input"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Customer operations portal"
            required
          />

          <label className="field-label" htmlFor="dashboard-description">
            What business does this app support?
          </label>
          <textarea
            id="dashboard-description"
            className="text-area"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="AI-built portal for onboarding, support, and customer operations"
            rows={4}
            required
          />

          <button className="btn btn-primary" type="submit" disabled={creating || !displayName.trim() || !description.trim()}>
            {creating ? <><LoaderCircle className="icon-inline spinning" /> Creating workspace</> : <>Create workspace <ArrowRight className="icon-inline" /></>}
          </button>
        </form>
      </Overlay>

      <Overlay
        open={checksOpen}
        onClose={() => setChecksOpen(false)}
        eyebrow="Evidence model"
        title="What Vhoop looks at"
      >
        <div className="overlay-content">
          <p className="paper-copy">Vhoop separates what can be proven statically from what needs paid runtime proof. The free check never executes customer code.</p>
          <div className="check-topic-grid">
            {[
              ['Source evidence', 'Repository, uploaded bundle, source notes, and managed continuity.'],
              ['Build readiness', 'Manifests, lockfiles, CI, build scripts, deploy hints, and reproducibility evidence.'],
              ['AI-change safety', 'Tests, contracts, schema validation, generated-code risk, and handoff clarity.'],
              ['Security and access', 'Auth/session evidence, environment hygiene, hardcoded secret signals, and customer-trust flows.'],
              ['Operations', 'Monitoring, runbooks, incident response, ownership, rollback, backups, and recurring checks.'],
              ['Runtime proof', 'Paid plans can run bounded build, test, typecheck, CI, and health-probe profiles in isolation.'],
            ].map(([title, copy]) => (
              <article key={title} className="paper-box">
                <h4>{title}</h4>
                <p className="paper-copy">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </Overlay>
    </InternalAppLayout>
  )
}

function LiveWorkspaceView() {
  const { workspaceId = '' } = useParams()
  const { userLabel, profile } = useAuthSession()
  const [detail, setDetail] = useState<VhoopWorkspaceDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [runningAssessment, setRunningAssessment] = useState(false)
  const [runningPaidPlan, setRunningPaidPlan] = useState<VhoopPlanId | null>(null)
  const [entitlements, setEntitlements] = useState<VhoopEntitlementsResponse | null>(null)
  const [uploading, setUploading] = useState(false)
  const [savingSource, setSavingSource] = useState(false)
  const [files, setFiles] = useState<FileList | null>(null)
  const [sourceKind, setSourceKind] = useState<OnboardingSourceKind>('upload')
  const [githubRepoUrl, setGithubRepoUrl] = useState('')
  const [deploymentUrl, setDeploymentUrl] = useState('')
  const [notes, setNotes] = useState('')
  const [reportSettingsOpen, setReportSettingsOpen] = useState(false)
  const [evidenceCaptureSource, setEvidenceCaptureSource] = useState<VhoopEvidenceCaptureSource>('browser')
  const [evidenceCaptureLabel, setEvidenceCaptureLabel] = useState('')
  const [evidencePackJson, setEvidencePackJson] = useState('')
  const [submittingEvidencePack, setSubmittingEvidencePack] = useState(false)
  const [promotingTarget, setPromotingTarget] = useState<'forge' | 'contextful' | null>(null)
  const [promotionResult, setPromotionResult] = useState<VhoopPromotionResponse | null>(null)

  useEffect(() => {
    let active = true

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [result, entitlementResult] = await Promise.all([
          getWorkspace(workspaceId),
          getWorkspaceEntitlements(workspaceId).catch(() => null),
        ])
        if (active) {
          setDetail(result)
          setEntitlements(entitlementResult)
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : 'Could not load application')
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      active = false
    }
  }, [workspaceId])

  useEffect(() => {
    if (!workspaceId || !isActiveAnalysis(detail?.report)) {
      return undefined
    }
    let active = true
    const refresh = () => {
      void getWorkspace(workspaceId)
        .then((result) => {
          if (active) {
            setDetail(result)
            if (result.report?.status !== 'failed') {
              setError(null)
            }
          }
        })
        .catch((err) => {
          if (active) {
            setError(err instanceof Error ? err.message : 'Could not refresh application')
          }
        })
    }
    refresh()
    const intervalId = window.setInterval(refresh, 2000)
    return () => {
      active = false
      window.clearInterval(intervalId)
    }
  }, [workspaceId, detail?.report?.run_id, detail?.report?.status])

  useEffect(() => {
    if (!detail) {
      return
    }
    const source = workspaceSourceCapture(detail)
    setSourceKind(source.source_kind)
    setGithubRepoUrl(source.github_repo_url)
    setDeploymentUrl(source.deployment_url)
    setNotes(source.notes)
  }, [detail?.workspace.updated_at])

  async function handleSourceSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSavingSource(true)
    setError(null)
    try {
      const result = await updateWorkspaceSource(workspaceId, {
        source_kind: sourceKind,
        github_repo_url: githubRepoUrl || null,
        deployment_url: deploymentUrl || null,
        notes: notes || null,
      })
      setDetail(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save source details')
    } finally {
      setSavingSource(false)
    }
  }

  async function handleUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!files || !files.length) {
      setError('Choose at least one file or ZIP before uploading')
      return
    }
    setUploading(true)
    setError(null)
    try {
      const result = await uploadWorkspaceAssets(workspaceId, Array.from(files))
      setDetail(result)
      setFiles(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  async function handleEvidencePackSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    let parsedPack: Record<string, unknown>
    try {
      const parsed = JSON.parse(evidencePackJson)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Evidence Pack JSON must be an object')
      }
      parsedPack = parsed as Record<string, unknown>
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Evidence Pack JSON is invalid')
      return
    }

    setSubmittingEvidencePack(true)
    setError(null)
    try {
      const result = await submitEvidencePack(workspaceId, {
        capture_source: evidenceCaptureSource,
        capture_label: evidenceCaptureLabel || undefined,
        evidence_pack: parsedPack,
      })
      setDetail(result)
      setEvidencePackJson('')
      setEvidenceCaptureLabel('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Evidence Pack intake failed')
    } finally {
      setSubmittingEvidencePack(false)
    }
  }

  async function handleAssessment() {
    setRunningAssessment(true)
    setError(null)
    try {
      const report = await runQuickAssessment(workspaceId)
      setDetail((current) => current ? {
        ...current,
        report,
        workspace: {
          ...current.workspace,
          status: report.status === 'completed' ? 'assessed' : 'assessing',
          latest_run_id: report.run_id,
        },
      } : current)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setRunningAssessment(false)
    }
  }

  async function handlePromotion(target: 'forge' | 'contextful') {
    setPromotingTarget(target)
    setError(null)
    try {
      const result = await promoteWorkspace(workspaceId, {
        target,
        reason: target === 'forge' ? 'Turn VHoop evidence into managed source work' : 'Promote VHoop evidence into a diligence workspace',
      })
      setPromotionResult(result)
      setDetail((current) => {
        if (!current) {
          return current
        }
        const existingPromotions = Array.isArray(current.workspace.intake_summary.promotions)
          ? current.workspace.intake_summary.promotions
          : []
        return {
          ...current,
          workspace: {
            ...current.workspace,
            intake_summary: {
              ...current.workspace.intake_summary,
              promotions: [...existingPromotions, result],
            },
          },
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Promotion failed')
    } finally {
      setPromotingTarget(null)
    }
  }

  async function handlePaidAssessment(planId: VhoopPlanId, executionProfiles: VhoopExecutionProfileId[]) {
    setRunningPaidPlan(planId)
    setError(null)
    try {
      const report = await runDeepAssessment(workspaceId, {
        plan_id: planId,
        execution_profiles: executionProfiles,
        use_agent: true,
      })
      setDetail((current) => current ? {
        ...current,
        report,
        workspace: {
          ...current.workspace,
          status: report.status === 'completed' ? 'assessed' : 'assessing',
          latest_run_id: report.run_id,
        },
      } : current)
      const entitlementResult = await getWorkspaceEntitlements(workspaceId).catch(() => null)
      setEntitlements(entitlementResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Paid proof run failed')
    } finally {
      setRunningPaidPlan(null)
    }
  }

  if (loading) {
    return (
      <Shell>
        <main className="container auth-shell">
          <section className="center-card">
            <span className="eyebrow"><LoaderCircle className="icon-inline spinning" /> Loading</span>
            <h1>Opening application hub</h1>
          </section>
        </main>
      </Shell>
    )
  }

  if (!detail) {
    return (
      <Shell>
        <main className="container auth-shell">
          <section className="center-card">
            <span className="eyebrow">Application unavailable</span>
            <h1>This application hub could not be loaded.</h1>
            <p>{error || 'Try starting a new snapshot.'}</p>
            <Link className="btn btn-primary" to="/signup">Start a new application</Link>
          </section>
        </main>
      </Shell>
    )
  }

  const { workspace, report } = detail
  const managedSource = workspaceManagedSource(detail)
  const sourceCapture = workspaceSourceCapture(detail)
  const repoPath = workspaceRepoPath(detail)
  const assets = workspaceAssets(detail)
  const evidencePacks = workspaceEvidencePacks(detail)
  const sourceSnapshots = workspaceSourceSnapshots(detail)
  const latestProgress = report?.progress[report.progress.length - 1]
  const accountPlan = entitlements?.account_plan?.plan_id || entitlements?.current_plan?.plan_id || report?.plan_id || 'free'
  const canRunMidProof = canUsePlan(accountPlan, 'operate')
  const canRunTopProof = canUsePlan(accountPlan, 'partner')
  const midProfiles: VhoopExecutionProfileId[] = ['manifest_validate', 'typecheck', 'lint', 'test', 'build']
  const topProfiles: VhoopExecutionProfileId[] = ['manifest_validate', 'typecheck', 'lint', 'test', 'build', 'start_probe', 'ci_simulation']

  return (
    <InternalAppLayout current="workspaces" userLabel={userLabel} profile={profile}>
      <main className="workspace-shell">
        <section className="workspace-compact-header">
          <div className="workspace-compact-main">
            <div className="workspace-compact-title-row">
              <span className="eyebrow compact-eyebrow"><BriefcaseBusiness className="icon-inline" /> Application Hub</span>
              {report?.status === 'completed' ? (
                <span className="workspace-compact-score">Score {report.score ?? 0} · {report.readiness_label}</span>
              ) : (
                <span className="workspace-compact-score">{latestProgress?.label || 'Ready for source evidence'}</span>
              )}
            </div>
            <h1>{workspace.display_name}</h1>
            <p>{workspace.description || 'Private operating area for your app, evidence, proof, and next steps.'}</p>
            <div className="hero-chip-row compact-chip-row">
              <span className="hero-chip">{workspace.status}</span>
              {repoPath ? <span className="hero-chip">Managed source attached</span> : null}
              {sourceCapture.github_repo_url ? <span className="hero-chip">GitHub repo linked</span> : null}
              {assets.length ? <span className="hero-chip">{assets.length} file{assets.length === 1 ? '' : 's'} uploaded</span> : null}
              {evidencePacks.length ? <span className="hero-chip">{evidencePacks.length} Evidence Pack{evidencePacks.length === 1 ? '' : 's'}</span> : null}
              {sourceSnapshots.length ? <span className="hero-chip">{sourceSnapshots.length} source snapshot{sourceSnapshots.length === 1 ? '' : 's'}</span> : null}
            </div>
          </div>
          <div className="workspace-compact-actions">
            <button className="btn btn-secondary btn-compact" type="button" onClick={() => setReportSettingsOpen((open) => !open)} aria-expanded={reportSettingsOpen}>
              Add project sources <ChevronDown className={`icon-inline ${reportSettingsOpen ? 'rotate-180' : ''}`} />
            </button>
            <button className="btn btn-primary btn-compact" type="button" onClick={handleAssessment} disabled={runningAssessment || isActiveAnalysis(report) || !hasSavedSource(detail)}>
              {runningAssessment ? <><LoaderCircle className="icon-inline spinning" /> Starting</> : report?.status === 'queued' || report?.status === 'running' ? <>Snapshot running</> : report?.status === 'completed' ? <>Run again</> : <>Generate operating snapshot</>}
            </button>
            <button className="btn btn-secondary btn-compact" type="button" onClick={() => handlePaidAssessment('operate', midProfiles)} disabled={!canRunMidProof || Boolean(runningPaidPlan) || isActiveAnalysis(report) || !hasSavedSource(detail)} title={canRunMidProof ? 'Run mid-tier isolated proof profiles' : 'Requires Operate System'}>
              {runningPaidPlan === 'operate' ? <><LoaderCircle className="icon-inline spinning" /> Mid proof</> : <>Run mid proof</>}
            </button>
            <button className="btn btn-secondary btn-compact" type="button" onClick={() => handlePaidAssessment('partner', topProfiles)} disabled={!canRunTopProof || Boolean(runningPaidPlan) || isActiveAnalysis(report) || !hasSavedSource(detail)} title={canRunTopProof ? 'Run the full Partner Ops proof suite' : 'Requires Partner Ops'}>
              {runningPaidPlan === 'partner' ? <><LoaderCircle className="icon-inline spinning" /> Top suite</> : <>Run top suite</>}
            </button>
          </div>
        </section>

        {error ? <div className="error-banner workspace-error">{error}</div> : null}

        {reportSettingsOpen ? (
          <section className="report-settings-panel">
            <div className="report-settings-head">
              <div>
                <span className="panel-label">Report settings</span>
                <h2>Source evidence for the next snapshot</h2>
                <p>Attach the code base, repository, or runtime context Vhoop should use when it re-runs the operating review.</p>
                <div className="trace-summary compact-plan-summary">
                  <span>Account plan: {entitlements?.account_plan?.name || accountPlan}</span>
                  <span>Mid proof: {canRunMidProof ? 'available' : 'locked'}</span>
                  <span>Top suite: {canRunTopProof ? 'available' : 'locked'}</span>
                </div>
              </div>
              <button className="btn btn-secondary btn-compact" type="button" onClick={() => setReportSettingsOpen(false)}>Close</button>
            </div>

            <div className="report-settings-grid">
              <section className="mini-surface source-surface">
                <h3>Source details</h3>
                <form className="workflow-form" onSubmit={handleSourceSave}>
                  <div className="choice-grid">
                    <button type="button" className={`choice-card${sourceKind === 'upload' ? ' choice-card-active' : ''}`} onClick={() => setSourceKind('upload')}>
                      <FolderUp className="icon-inline" />
                      <span>Files</span>
                    </button>
                    <button type="button" className={`choice-card${sourceKind === 'repo' ? ' choice-card-active' : ''}`} onClick={() => setSourceKind('repo')}>
                      <Github className="icon-inline" />
                      <span>GitHub</span>
                    </button>
                    <button type="button" className={`choice-card${sourceKind === 'url' ? ' choice-card-active' : ''}`} onClick={() => setSourceKind('url')}>
                      <Globe className="icon-inline" />
                      <span>URL</span>
                    </button>
                  </div>
                  <label className="field-label" htmlFor="workspace-github-url">GitHub repo URL</label>
                  <input id="workspace-github-url" className="text-input" value={githubRepoUrl} onChange={(event) => setGithubRepoUrl(event.target.value)} placeholder="https://github.com/your-org/customer-portal" />
                  <label className="field-label" htmlFor="workspace-live-url">Live or staging URL</label>
                  <input id="workspace-live-url" className="text-input" value={deploymentUrl} onChange={(event) => setDeploymentUrl(event.target.value)} placeholder="https://portal.example.com" />
                  <label className="field-label" htmlFor="workspace-notes">Notes</label>
                  <textarea id="workspace-notes" className="text-area" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Launch pressure, customers, or operating context" rows={3} />
                  <button className="btn btn-secondary" type="submit" disabled={savingSource}>
                    {savingSource ? <><LoaderCircle className="icon-inline spinning" /> Saving</> : <>Save source details</>}
                  </button>
                </form>
              </section>

              <section className="mini-surface source-surface">
                <h3>Upload files</h3>
                <form className="workflow-form" onSubmit={handleUpload}>
                  <label className="field-label" htmlFor="workspace-upload">Upload project bundle</label>
                  <input id="workspace-upload" className="text-input" type="file" multiple onChange={(event) => setFiles(event.target.files)} />
                  <button className="btn btn-secondary" type="submit" disabled={uploading}>
                    {uploading ? <><LoaderCircle className="icon-inline spinning" /> Uploading</> : <>Upload files</>}
                  </button>
                </form>
                {assets.length ? (
                  <ul className="clean-list">
                    {assets.map((asset) => (
                      <li key={`${asset.filename}-${asset.size_bytes}`}>{asset.filename} · {asset.size_bytes} bytes</li>
                    ))}
                  </ul>
                ) : null}
              </section>

              <section className="mini-surface source-surface">
                <h3>Evidence Packs</h3>
                <form className="workflow-form" onSubmit={handleEvidencePackSubmit}>
                  <div className="capture-choice-grid" role="group" aria-label="Evidence capture source">
                    <button type="button" className={`choice-card${evidenceCaptureSource === 'browser' ? ' choice-card-active' : ''}`} onClick={() => setEvidenceCaptureSource('browser')}>
                      <Globe className="icon-inline" />
                      <span>Browser</span>
                    </button>
                    <button type="button" className={`choice-card${evidenceCaptureSource === 'office' ? ' choice-card-active' : ''}`} onClick={() => setEvidenceCaptureSource('office')}>
                      <FileText className="icon-inline" />
                      <span>Office</span>
                    </button>
                    <button type="button" className={`choice-card${evidenceCaptureSource === 'mobile_share' ? ' choice-card-active' : ''}`} onClick={() => setEvidenceCaptureSource('mobile_share')}>
                      <Smartphone className="icon-inline" />
                      <span>Mobile</span>
                    </button>
                    <button type="button" className={`choice-card${evidenceCaptureSource === 'git' ? ' choice-card-active' : ''}`} onClick={() => setEvidenceCaptureSource('git')}>
                      <Github className="icon-inline" />
                      <span>Git</span>
                    </button>
                    <button type="button" className={`choice-card${evidenceCaptureSource === 'upload' ? ' choice-card-active' : ''}`} onClick={() => setEvidenceCaptureSource('upload')}>
                      <FolderUp className="icon-inline" />
                      <span>Upload</span>
                    </button>
                  </div>
                  <label className="field-label" htmlFor="workspace-evidence-label">Capture label</label>
                  <input id="workspace-evidence-label" className="text-input" value={evidenceCaptureLabel} onChange={(event) => setEvidenceCaptureLabel(event.target.value)} placeholder="Board launch memo" />
                  <label className="field-label" htmlFor="workspace-evidence-pack">Evidence Pack JSON</label>
                  <textarea id="workspace-evidence-pack" className="text-area evidence-pack-input" value={evidencePackJson} onChange={(event) => setEvidencePackJson(event.target.value)} placeholder='{"schemaVersion":"edgekit.evidence-pack.v1","claims":[]}' rows={8} />
                  <button className="btn btn-secondary" type="submit" disabled={submittingEvidencePack}>
                    {submittingEvidencePack ? <><LoaderCircle className="icon-inline spinning" /> Attaching</> : <><Share2 className="icon-inline" /> Attach Evidence Pack</>}
                  </button>
                </form>
                {evidencePacks.length ? (
                  <ul className="clean-list">
                    {evidencePacks.map((pack) => (
                      <li key={pack.pack_id}>{pack.capture_label} · {pack.capture_source} · {pack.claim_count} claim{pack.claim_count === 1 ? '' : 's'}</li>
                    ))}
                  </ul>
                ) : null}
                {sourceSnapshots.length ? (
                  <div className="trace-summary evidence-snapshot-summary">
                    {sourceSnapshots.slice(0, 3).map((snapshot) => (
                      <span key={snapshot.snapshot_id}>{snapshot.label}: {snapshot.storage_mode}</span>
                    ))}
                  </div>
                ) : null}
              </section>

              <section className="mini-surface source-surface">
                <h3>Managed source</h3>
                {managedSource ? (
                  <div className="repo-grid">
                    <div className="repo-box">
                      <span className="field-label">Canonical path</span>
                      <code>{managedSource.canonical_path}</code>
                    </div>
                    {managedSource.forge_workspace_url ? (
                      <a className="btn btn-secondary" href={managedSource.forge_workspace_url} target="_blank" rel="noreferrer">
                        Open managed source
                      </a>
                    ) : null}
                  </div>
                ) : (
                  <p className="surface-copy">No managed source repository is linked yet.</p>
                )}
              </section>
            </div>
          </section>
        ) : null}

        <section className="workspace-result-panel workspace-result-panel-full">
          <div className="dashboard-section-head report-result-head">
            <div>
              <span className="paper-kicker">Latest test result</span>
              <h2>{report?.status === 'completed' ? 'Operating snapshot result' : 'No completed snapshot yet'}</h2>
            </div>
          </div>
          <div className="workspace-followup-band">
            <div>
              <span className="panel-label">Follow-up paths</span>
              <p>{sourceSnapshots.length ? `${sourceSnapshots.length} source snapshot${sourceSnapshots.length === 1 ? '' : 's'} ready for governed follow-up.` : 'Follow-ups become stronger once source snapshots or managed source exist.'}</p>
            </div>
            <div className="workspace-followup-actions">
              <button className="btn btn-secondary btn-compact" type="button" onClick={() => handlePromotion('forge')} disabled={promotingTarget !== null || !hasSavedSource(detail)}>
                {promotingTarget === 'forge' ? <><LoaderCircle className="icon-inline spinning" /> Forge</> : <>Promote to Forge</>}
              </button>
              <button className="btn btn-secondary btn-compact" type="button" onClick={() => handlePromotion('contextful')} disabled={promotingTarget !== null || !hasSavedSource(detail)}>
                {promotingTarget === 'contextful' ? <><LoaderCircle className="icon-inline spinning" /> Contextful</> : <>Promote to Contextful</>}
              </button>
            </div>
            {promotionResult ? (
              <div className="trace-summary promotion-result">
                <span>{promotionResult.target}: {promotionResult.status}</span>
                <span>{promotionResult.summary}</span>
              </div>
            ) : null}
          </div>
          {report?.error_message ? <div className="error-banner">{report.error_message}</div> : null}
          {report?.status === 'completed' ? (
            <ReportSnapshot report={report} />
          ) : (
            <div className="empty-report">
              <ShieldAlert className="empty-icon" />
              <h3>{report?.status === 'failed' ? 'Snapshot failed' : 'Run the first operating snapshot'}</h3>
              <p>{report?.status === 'failed' ? (report.error_message || 'Vhoop could not finish the operating snapshot.') : 'Add a repository, live URL, upload, or Evidence Pack first. Then Vhoop will show the result here directly, without a workflow wrapper.'}</p>
              {latestProgress ? <p>{latestProgress.label}</p> : null}
            </div>
          )}
        </section>
      </main>
    </InternalAppLayout>
  )
}

function SampleWorkspaceView() {
  const detail = sampleWorkspaceDetail
  const report = detail.report!

  return (
    <Shell>
      <main className="container sample-shell">
        <section className="workspace-hero">
          <div className="workspace-hero-main">
            <span className="eyebrow"><ShieldAlert className="icon-inline" /> Sample operating snapshot</span>
            <h1>{detail.workspace.display_name}</h1>
            <p>A sample Vhoop output for a company that wants to know whether the app can be hosted, operated, recovered, and improved.</p>
          </div>
          <div className="hero-score-card">
            <span className="panel-label">Score</span>
            <div className="score-number">{report.score}</div>
            <p>{report.readiness_label}</p>
          </div>
        </section>
        <ReportSnapshot report={report} />
      </main>
    </Shell>
  )
}

function LoginView() {
  const { loginHref } = useAuthLinks('/dashboard')

  return (
    <Shell>
      <main className="container auth-shell">
        <div className="auth-split">
          <section className="auth-hero">
            <span className="eyebrow"><LockKeyhole className="icon-inline" /> Secure access</span>
            <h1>Welcome back to VHoop.</h1>
            <p>Your applications, assessments, and open questions, together in one workspace.</p>
          </section>
          <section className="auth-panel">
            <h2>Log in</h2>
            <p>Your next assessment starts here.</p>
            <a className="btn btn-primary full-width" href={loginHref}>Continue to log in</a>
          </section>
        </div>
      </main>
    </Shell>
  )
}

function UpgradePlaceholder() {
  const { tierId = 'launch' } = useParams()
  const upgradePath = fallbackUpgradePaths().find((path) => path.tier_id === tierId) ?? fallbackUpgradePaths()[0]

  return (
    <Shell>
      <main className="container auth-shell">
        <section className="center-card center-card-wide">
          <span className="eyebrow">Operating plan</span>
          <h1>{upgradePath.name}</h1>
          <p>{upgradePath.outcome_summary}</p>
          <div className="mini-surface">
            <h3>{upgradePath.tagline}</h3>
            <ul className="clean-list">
              {upgradePath.deliverables.map((deliverable) => <li key={`${upgradePath.tier_id}-${deliverable}`}>{deliverable}</li>)}
            </ul>
          </div>
          <p className="surface-copy">Checkout is still mocked for now.</p>
          <div className="hero-actions">
            <Link className="btn btn-primary" to="/signup">{upgradePath.cta_label}</Link>
            <Link className="btn btn-secondary" to="/workspace/sample">Review sample snapshot</Link>
          </div>
        </section>
      </main>
    </Shell>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingView />} />
      <Route path="/features" element={<FeaturesView />} />
      <Route path="/use-cases" element={<UseCasesView />} />
      <Route path="/plans" element={<PlansView />} />
      <Route path="/security" element={<SecurityView />} />
      <Route path="/faq" element={<FaqView />} />
      <Route path="/start" element={<SignupView standalone />} />
      <Route path="/start/details" element={<SignupView standalone />} />
      <Route path="/start/signup" element={<SignupView standalone />} />
      <Route path="/signup" element={<SignupView standalone />} />
      <Route path="/login" element={<LoginView />} />
      <Route path="/dashboard" element={<DashboardView />} />
      <Route path="/workspaces" element={<DashboardView view="workspaces" />} />
      <Route path="/reports" element={<DashboardView view="reports" />} />
      <Route path="/settings" element={<DashboardView view="settings" />} />
      <Route path="/workspace/sample" element={<SampleWorkspaceView />} />
      <Route path="/workspace/:workspaceId" element={<LiveWorkspaceView />} />
      <Route path="/upgrade/:tierId" element={<UpgradePlaceholder />} />
    </Routes>
  )
}
